import type { PreOrder } from "./preOrder";

export enum WhatsAppInstanceStatusEnum {
  CREATED = 0,
  CONNECTING = 1,
  CONNECTED = 2,
  DISCONNECTED = 3,
  ERROR = 4,
}

export const WhatsAppInstanceStatusMap: Record<
  WhatsAppInstanceStatusEnum,
  string
> = {
  [WhatsAppInstanceStatusEnum.CREATED]: "Created",
  [WhatsAppInstanceStatusEnum.CONNECTING]: "Connecting",
  [WhatsAppInstanceStatusEnum.CONNECTED]: "Connected",
  [WhatsAppInstanceStatusEnum.DISCONNECTED]: "Disconnected",
  [WhatsAppInstanceStatusEnum.ERROR]: "Error",
};

export enum WhatsAppMessageTypeEnum {
  UNKNOWN = 0,
  CONVERSATION = 1,
  IMAGE_MESSAGE = 2,
  VIDEO_MESSAGE = 3,
  DOCUMENT_MESSAGE = 4,
  AUDIO_MESSAGE = 5,
  STICKER_MESSAGE = 6,
  LOCATION_MESSAGE = 7,
  CONTACT_MESSAGE = 8,
  LIST_RESPONSE_MESSAGE = 9,
  BUTTONS_RESPONSE_MESSAGE = 10,
  ORDER_MESSAGE = 11,
  SYSTEM_MESSAGE = 12,
  REACTION_MESSAGE = 13,
  EDITED_MESSAGE = 14,
  INTERACTIVE_MESSAGE = 15,
  POLL_CREATION_MESSAGE_V3 = 16,
}

export const WhatsAppMessageTypeMap: Record<WhatsAppMessageTypeEnum, string> = {
  [WhatsAppMessageTypeEnum.UNKNOWN]: "Unknown",
  [WhatsAppMessageTypeEnum.CONVERSATION]: "Text",
  [WhatsAppMessageTypeEnum.IMAGE_MESSAGE]: "Image",
  [WhatsAppMessageTypeEnum.VIDEO_MESSAGE]: "Video",
  [WhatsAppMessageTypeEnum.DOCUMENT_MESSAGE]: "Document",
  [WhatsAppMessageTypeEnum.AUDIO_MESSAGE]: "Audio",
  [WhatsAppMessageTypeEnum.STICKER_MESSAGE]: "Sticker",
  [WhatsAppMessageTypeEnum.LOCATION_MESSAGE]: "Location",
  [WhatsAppMessageTypeEnum.CONTACT_MESSAGE]: "Contact",
  [WhatsAppMessageTypeEnum.LIST_RESPONSE_MESSAGE]: "List Response",
  [WhatsAppMessageTypeEnum.BUTTONS_RESPONSE_MESSAGE]: "Buttons Response",
  [WhatsAppMessageTypeEnum.ORDER_MESSAGE]: "Order",
  [WhatsAppMessageTypeEnum.SYSTEM_MESSAGE]: "System",
  [WhatsAppMessageTypeEnum.REACTION_MESSAGE]: "Reaction",
  [WhatsAppMessageTypeEnum.EDITED_MESSAGE]: "Edited",
  [WhatsAppMessageTypeEnum.INTERACTIVE_MESSAGE]: "Interactive",
  [WhatsAppMessageTypeEnum.POLL_CREATION_MESSAGE_V3]: "Poll",
};

export enum MessageStatusTypeEnum {
  PENDING_SEGMENTATION = 0,
  PROCESSING = 1,
  COMPLETED = 2,
  FAILED = 3,
  SEGMENTATION_IGNORED = 4,
  QUEUED = 5,
}

export const MessageStatusTypeMap: Record<MessageStatusTypeEnum, string> = {
  [MessageStatusTypeEnum.PENDING_SEGMENTATION]: "Pending Segmentation",
  [MessageStatusTypeEnum.PROCESSING]: "Processing",
  [MessageStatusTypeEnum.COMPLETED]: "Completed",
  [MessageStatusTypeEnum.FAILED]: "Failed",
  [MessageStatusTypeEnum.SEGMENTATION_IGNORED]: "Segmentation Ignored",
  [MessageStatusTypeEnum.QUEUED]: "Queued",
};

// --- Message Key Info --- (NEW)
export interface MessageKeyInfo {
  id?: string | null;
  remote_jid?: string | null;
  from_me?: boolean | null;
  participant?: string | null; // For group messages
}

// --- Detailed Message Content Interfaces --- (NEW)
export type ContentType =
  | "text"
  | "edited"
  | "reaction"
  | "image"
  | "video"
  | "audio"
  | "document"
  | "sticker"
  | "location"
  | "contact"
  | "system";

export interface ConversationContent {
  contentType: "text";
  text: string;
  key_info?: MessageKeyInfo | null;
}

export interface EditedMessageContent {
  contentType: "edited";
  original_message_key_id: string;
  new_text: string;
  edit_timestamp_ms?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface ReactionMessageContent {
  contentType: "reaction";
  reacted_message_key_id: string;
  text: string; // The reaction emoji
  sender_timestamp_ms?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface ImageMessageContent {
  contentType: "image";
  url?: string | null;
  mimetype?: string | null;
  caption?: string | null;
  jpeg_thumbnail?: string | null;
  file_length?: string | null;
  height?: number | null;
  width?: number | null;
  // Media display fields
  download_status?: string | null;
  download_error?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface VideoMessageContent {
  contentType: "video";
  url?: string | null;
  mimetype?: string | null;
  caption?: string | null;
  jpeg_thumbnail?: string | null;
  seconds?: number | null;
  file_length?: string | null;
  height?: number | null;
  width?: number | null;
  // Media display fields
  download_status?: string | null;
  download_error?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface AudioMessageContent {
  contentType: "audio";
  url?: string | null;
  mimetype?: string | null;
  is_ptt?: boolean | null;
  seconds?: number | null;
  file_length?: string | null;
  // Media display fields
  download_status?: string | null;
  download_error?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface DocumentMessageContent {
  contentType: "document";
  url?: string | null;
  mimetype?: string | null;
  title?: string | null;
  file_length?: string | null;
  page_count?: number | null;
  jpeg_thumbnail?: string | null;
  // Media display fields
  download_status?: string | null;
  download_error?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface StickerMessageContent {
  contentType: "sticker";
  url?: string | null;
  mimetype?: string | null;
  height?: number | null;
  width?: number | null;
  file_length?: string | null;
  // Media display fields
  download_status?: string | null;
  download_error?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface LocationMessageContent {
  contentType: "location";
  degrees_latitude?: number | null;
  degrees_longitude?: number | null;
  name?: string | null;
  address?: string | null;
  url?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface ContactMessageContent {
  contentType: "contact";
  display_name?: string | null;
  vcard?: string | null;
  key_info?: MessageKeyInfo | null;
}

export interface SystemMessageContent {
  contentType: "system";
  text: string;
  key_info?: MessageKeyInfo | null;
}

// Note: Removed UnsupportedMessageContent - all unknown types are now treated as documents

export type MessageContent =
  | ConversationContent
  | EditedMessageContent
  | ReactionMessageContent
  | ImageMessageContent
  | VideoMessageContent
  | AudioMessageContent
  | DocumentMessageContent
  | StickerMessageContent
  | LocationMessageContent
  | ContactMessageContent
  | SystemMessageContent;

export interface WhatsAppMessageBase {
  message_type?: WhatsAppMessageTypeEnum;
  is_from_me?: boolean;
  media_url?: string | null;
  timestamp?: number | null;
  from_jid?: string | null;
  from_name?: string | null;
  from_number?: string | null;
}

export interface WhatsAppMessageCreate extends WhatsAppMessageBase {
  id: string;
  user_id: string;
  chat_jid: string;
  content: MessageContent;
  status?: MessageStatusTypeEnum;
}

export interface WhatsAppMessageSendText {
  instance_name: string;
  jid: string;
  content: Record<string, any>;
}

export interface WhatsAppMessageUpdate {
  ai_analysis?: Record<string, any> | null;
  media_extraction?: Record<string, any> | null;
  status?: MessageStatusTypeEnum;
}

export interface WhatsAppMessageResponse extends WhatsAppMessageBase {
  id: string;
  user_id: string;
  chat_jid?: string | null;
  content: MessageContent;
  ai_analysis?: Record<string, any> | null;
  media_extraction?: Record<string, any> | null;
  status: MessageStatusTypeEnum;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface WhatsAppInstanceBase {
  name: string;
  number?: string | null;
}

export interface WhatsAppInstanceCreate extends WhatsAppInstanceBase {
  group_id?: string | null;
}

export interface WhatsAppInstanceUpdate {
  id?: string | null;
  status?: WhatsAppInstanceStatusEnum | null;
  group_id?: string | null;
}

export interface WhatsAppInstanceResponse extends WhatsAppInstanceBase {
  id: string;
  group_id?: string | null;
  status: WhatsAppInstanceStatusEnum;
  owner_jid?: string | null;
  enabled: boolean;
  deleted_at?: string | null;
  deletion_reason?: string | null;
}

export interface WhatsAppInstanceConnectionState {
  instance_name: string;
  state: string;
}

export interface WhatsAppApiResponse {
  success: boolean;
  message: string;
  data?: Record<string, any> | null;
}

export interface WhatsAppChatBase {
  jid: string;
  name: string;
  instance_id: string;
  partner_id?: string | null;
}

export interface WhatsAppChatResponse extends WhatsAppChatBase {
  id: string;
  uid: string; // UUID from backend
  user_id?: string | null;
  is_group?: boolean;
  group_id?: string | null;
  number?: string | null;
  messages?: WhatsAppMessageResponse[] | null;

  // Conversation tracking fields
  last_message_content?: string | null;
  last_message_date?: string | null;
  last_message_direction?: string | null; // "inbound" or "outbound"
  unread_count: number;

  // AI context
  ai_context?: string | null;

  // AI analysis toggle — when false, inbound messages skip AI workflow
  ai_analysis_enabled?: boolean;

  // Metadata for AI comments and flags
  chat_metadata?: Record<string, any> | null;

  // Archive status
  archived: boolean;

  // Disabled chats are hidden from the chat list and the processor drops
  // inbound messages for them. Optional for backward-compat with API
  // responses that pre-date the column.
  enabled?: boolean;

  // Share information
  is_shared?: boolean;
  is_owned?: boolean;
  shared_by_user_id?: string | null;
  shared_by_name?: string | null;
  share_permissions?: {
    can_read: boolean;
    can_send: boolean;
    can_link_partner: boolean;
  } | null;
}

export interface WhatsAppChatDB extends WhatsAppChatBase {
  id: string;
  user_id: string;
  is_group?: boolean;
  number?: string | null;
}

export interface WhatsAppChatCreate extends WhatsAppChatBase {}

export interface WhatsAppChatUpdate {
  name?: string | null;
  partner_id?: string | null;
}

export interface WhatsAppChat {
  id: string;
  chat_jid: string;
  number: string | null;
  name: string;
  is_group: boolean;
  instance_name?: string;
  instance_id: string;
  partner_id?: string | null;
}

export interface EvolutionInstanceData {
  id: string;
  name: string;
  connectionStatus: string;
  ownerJid?: string | null;
  profileName?: string | null;
  number?: string | null;
}

export interface QRCode {
  pairingCode?: string | null;
  code: string;
  base64: string;
  count: number;
}

export interface CreateInstanceResponse {
  instance: EvolutionInstanceData;
  qrcode: QRCode;
}

export interface InstanceSetupRequest {
  instance_name: string;
}

export interface WhatsAppMessageList {
  messages: WhatsAppMessageResponse[];
  pre_orders?: PreOrder[] | null;
  total?: number;
  pages?: number;
  current_page?: number;
}

export interface MessageContext {
  messages: WhatsAppMessageResponse[];
  related_orders: Record<string, any>[];
  window_size: number;
  oldest_message_timestamp?: number | null;
  newest_message_timestamp?: number | null;
}

export interface WhatsAppChatsWrapper {
  chats: WhatsAppChatResponse[];
}

export interface WhatsAppChatList {
  chats: WhatsAppChatResponse[];
  total: number;
  pages: number;
  current_page: number;
  unread_preorder_counts: Record<string, number> | null;
  server_time?: string;
}

export interface WhatsAppChats {
  chats: WhatsAppChat[];
}
